import type { ReactNode } from 'react';
import MuiButton from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import { isRetryable } from './errors';

interface AsyncStateProps {
  loading?: boolean;
  error?: Error | null;
  /** True when the data loaded but there is nothing to show. */
  empty?: boolean;
  emptyMessage?: ReactNode;
  errorPrefix?: string;
  /** Shows a « Réessayer » button when the error is worth retrying (server or network). */
  onRetry?: () => void;
  children: ReactNode;
}

/** The loading / error / empty trio every data view needs, in one place. */
export function AsyncState({
  loading,
  error,
  empty,
  emptyMessage = 'Aucune donnée.',
  errorPrefix = 'Impossible de charger les données',
  onRetry,
  children,
}: AsyncStateProps) {
  if (error)
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Typography color="error">
          {errorPrefix} : {error.message}
        </Typography>
        {onRetry && isRetryable(error) && (
          <MuiButton size="small" variant="outlined" onClick={onRetry}>
            Réessayer
          </MuiButton>
        )}
      </div>
    );
  if (loading) return <LinearProgress aria-label="Chargement" />;
  if (empty)
    return <Typography color="textSecondary">{emptyMessage}</Typography>;
  return <>{children}</>;
}
